import React, { useState, useEffect } from 'react';
import './FormSetting.css';

const FormSetting = ({ element, onUpdate, elements }) => {
  const [formName, setFormName] = useState('');
  const [method, setMethod] = useState('get');
  const [fields, setFields] = useState([]);
  const [formElement, setFormElement] = useState(null);
  const [labelText, setLabelText] = useState('');
  const [inputName, setInputName] = useState('');
  const [inputType, setInputType] = useState('text');
  const [placeholder, setPlaceholder] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isAutofocus, setIsAutofocus] = useState(false);

  const isFormOrBlock = element.type === 'form' || element.type === 'form-block';
  const isLabel = element.type === 'label';
  const isInput = element.type === 'input';

  // Update form element and its properties whenever element or elements change
  useEffect(() => {
    if (isFormOrBlock) {
      let form;
      if (element.type === 'form') {
        form = element;
      } else if (element.type === 'form-block' && element.children?.length > 0) {
        form = elements.find(el => el.id === element.children[0]);
      }
      
      if (form) {
        setFormElement(form);
        setFormName(form.formName || 'Email Form');
        setMethod(form.method || 'get');
      }
    } else if (isLabel) {
      setLabelText(element.text || '');
    }

    if (isInput) {
      setInputName(element.name || '');
      setInputType(element.inputType || 'text');
      setPlaceholder(element.placeholder || '');
      setIsRequired(element.required || false);
      setIsAutofocus(element.autofocus || false);
    }
  }, [element, elements, isFormOrBlock, isLabel, isInput]);

  // Get form fields
  useEffect(() => {
    const getFormFields = () => {
      if (element.type === 'form-block') {
        const formElement = document.querySelector(`[data-builder-id="${element.children[0]}"]`);
        if (formElement) {
          return Array.from(formElement.children).map(child => {
            if (child.tagName.toLowerCase() === 'label') {
              return {
                id: child.dataset.builderId,
                type: 'label',
                label: child.textContent,
                name: child.textContent.toLowerCase().replace(':', '')
              };
            } else if (child.tagName.toLowerCase() === 'input') {
              return {
                id: child.dataset.builderId,
                type: child.type || 'text',
                name: child.name || child.type,
                label: child.previousElementSibling?.textContent || child.type
              };
            } else if (child.tagName.toLowerCase() === 'button' && child.type === 'submit') {
              return {
                id: child.dataset.builderId,
                type: 'submit',
                name: 'submit',
                label: child.textContent
              };
            }
            return null;
          }).filter(Boolean);
        }
      } else if (element.type === 'form') {
        return element.children.map(childId => {
          const child = document.querySelector(`[data-builder-id="${childId}"]`);
          if (child) {
            if (child.tagName.toLowerCase() === 'label') {
              return {
                id: childId,
                type: 'label',
                label: child.textContent,
                name: child.textContent.toLowerCase().replace(':', '')
              };
            } else if (child.tagName.toLowerCase() === 'input') {
              return {
                id: childId,
                type: child.type || 'text',
                name: child.name || child.type,
                label: child.previousElementSibling?.textContent || child.type
              };
            } else if (child.tagName.toLowerCase() === 'button' && child.type === 'submit') {
              return {
                id: childId,
                type: 'submit',
                name: 'submit',
                label: child.textContent
              };
            }
          }
          return null;
        }).filter(Boolean);
      }
      return [];
    };

    const formFields = getFormFields();
    setFields(formFields);
  }, [element, element.children]);

  const handleFormNameChange = (e) => {
    const newName = e.target.value;
    setFormName(newName);
    if (formElement) {
      const updatedElement = {
        ...formElement,
        formName: newName
      };
      onUpdate(updatedElement);
    }
  };

  const handleMethodChange = (e) => {
    const newMethod = e.target.value;
    setMethod(newMethod);
    if (formElement) {
      const updatedElement = {
        ...formElement,
        method: newMethod
      };
      onUpdate(updatedElement);
    }
  };

  const handleLabelTextChange = (e) => {
    const newText = e.target.value;
    setLabelText(newText);
    if (element.type === 'label') {
      const updatedElement = {
        ...element,
        text: newText
      };
      onUpdate(updatedElement);
    }
  };

  const handleInputNameChange = (e) => {
    const newName = e.target.value;
    setInputName(newName);
    if (isInput) {
      const updatedElement = {
        ...element,
        name: newName
      };
      onUpdate(updatedElement);
    }
  };

  const handleInputTypeChange = (e) => {
    const newType = e.target.value;
    setInputType(newType);
    if (isInput) {
      const updatedElement = {
        ...element,
        inputType: newType
      };
      onUpdate(updatedElement);
    }
  };

  const handlePlaceholderChange = (e) => {
    const newPlaceholder = e.target.value;
    setPlaceholder(newPlaceholder);
    if (isInput) {
      const updatedElement = {
        ...element,
        placeholder: newPlaceholder
      };
      onUpdate(updatedElement);
    }
  };

  const handleRequiredChange = (e) => {
    const newRequired = e.target.checked;
    setIsRequired(newRequired);
    if (isInput) {
      const updatedElement = {
        ...element,
        required: newRequired
      };
      onUpdate(updatedElement);
    }
  };

  const handleAutofocusChange = (e) => {
    const newAutofocus = e.target.checked;
    setIsAutofocus(newAutofocus);
    if (isInput) {
      const updatedElement = {
        ...element,
        autofocus: newAutofocus
      };
      onUpdate(updatedElement);
    }
  };

  if (isLabel) {
    return (
      <div className="form-settings">
        <div className="form-setting-group">
          <label>Label Text</label>
          <input
            type="text"
            value={labelText}
            onChange={handleLabelTextChange}
            placeholder="Enter label text"
            className="form-setting-input"
          />
        </div>
      </div>
    );
  }

  if (isInput) {
    return (
      <div className="form-settings">
        <div className="form-setting-group">
          <label>Input Name</label>
          <input
            type="text"
            value={inputName}
            onChange={handleInputNameChange}
            placeholder="Enter input name"
            className="form-setting-input"
          />
        </div>

        <div className="form-setting-group">
          <label>Input Type</label>
          <select 
            value={inputType}
            onChange={handleInputTypeChange}
            className="form-setting-select"
          >
            <option value="text">Plain Text</option>
            <option value="email">Email</option>
            <option value="password">Password</option>
            <option value="tel">Phone</option>
            <option value="number">Number</option>
            <option value="url">URL</option>
          </select>
        </div>

        <div className="form-setting-group">
          <label>Placeholder</label>
          <input
            type="text"
            value={placeholder}
            onChange={handlePlaceholderChange}
            placeholder="Enter placeholder text"
            className="form-setting-input"
          />
        </div>

        <div className="form-setting-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={handleRequiredChange}
              className="form-setting-checkbox"
            />
            Required Field
          </label>
        </div>

        <div className="form-setting-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isAutofocus}
              onChange={handleAutofocusChange}
              className="form-setting-checkbox"
            />
            Autofocus
          </label>
        </div>
      </div>
    );
  }

  if (isFormOrBlock) {
    return (
      <div className="form-settings">
        <div className="form-setting-group">
          <label>Form Name</label>
          <input
            type="text"
            value={formName}
            onChange={handleFormNameChange}
            placeholder="Enter form name"
            className="form-setting-input"
          />
        </div>

        <div className="form-setting-group">
          <label>Method</label>
          <select 
            value={method}
            onChange={handleMethodChange}
            className="form-setting-select"
          >
            <option value="get">GET</option>
            <option value="post">POST</option>
          </select>
        </div>

        <div className="form-setting-group">
          <div className="form-fields-title">
            <span>Form Fields</span>
            <span className="form-fields-count">{fields.length} fields</span>
          </div>
          <div className="form-fields-list">
            {fields.map((field, index) => (
              <div key={field.id} className="form-field-info">
                <span className="field-label">{field.label}</span>
                <span className="field-type">{field.type}</span>
              </div>
            ))}
            {fields.length === 0 && (
              <div className="form-field-info">
                <span className="field-label" style={{ color: '#666' }}>No fields added yet</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FormSetting; 